import styled from "@emotion/styled";
import type { ButtonProps } from "@mui/material";
import { Button, ButtonGroup, Grow, MenuItem, MenuList, Paper, Popper } from "@mui/material";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import type { MouseEvent, ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";

import { Loading, LoadingContainer } from "../common";
import { Icon } from "../icons";

type Option = Pick<ButtonProps, "onClick"> & {
  label: ReactNode;
  icon?: ReactNode;
};

type Props = ButtonProps & {
  busy: boolean;
  options: Option[];
};

const ContentContainer = styled.div<{ $showIcon: boolean }>`
  display: flex;
  gap: 8px;

  place-items: center;

  ${LoadingContainer} {
    width: initial;
  }
`;

const StyledButton = styled(Button)`
  flex: 0 0 auto;
  background: ${({ theme, variant }) => (variant === "outlined" ? theme.surface : undefined)};
`;

const StyledDropdownButton = styled(Button)`
  background: ${({ theme }) => theme.surface};
  && {
    min-width: 0;
    padding: 0 5px;
  }
`;

export const SplitButton = ({ busy, options, variant, disabled, ...rest }: Props) => {
  const dataTest = (rest as Record<string, unknown>)["data-test"];

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const handleClick = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      setAnchorEl(e.currentTarget);
    }
  };

  const handleClose = (event: Event) => {
    if (anchorEl && anchorEl.contains(event.target as HTMLElement)) return;

    setAnchorEl(null);
  };

  const handleMenuItemClick = useCallback(
    (index: number) => (e: React.MouseEvent) => {
      setSelectedIndex(index);
      setAnchorEl(null);
      options[index].onClick?.(e as React.MouseEvent<HTMLButtonElement>);
    },
    [options],
  );

  const selectedOption = useMemo(() => {
    if (options.length === 0) return null;
    if (selectedIndex >= options.length) return options[0];

    return options[selectedIndex];
  }, [options, selectedIndex]);

  const DropdownButton = (
    <StyledButton
      onClick={selectedOption?.onClick}
      variant={variant}
      disabled={disabled}
      data-test="split-button-action-button"
      {...rest}
    >
      <ContentContainer $showIcon={!!selectedOption?.icon}>
        {busy ? <Loading size={16} /> : selectedOption?.icon}
        {selectedOption?.label}
      </ContentContainer>
    </StyledButton>
  );

  const open = Boolean(anchorEl);

  return options.length === 1 ? (
    DropdownButton
  ) : (
    <>
      <ButtonGroup disabled={disabled} variant={variant} aria-label="split button" data-test={dataTest}>
        {DropdownButton}
        <StyledDropdownButton
          size="small"
          aria-controls={open ? "split-button-menu" : undefined}
          aria-expanded={open ? "true" : undefined}
          aria-haspopup="menu"
          onClick={handleClick}
          data-test="split-button-menu-button"
        >
          <Icon icon="angle-down" />
        </StyledDropdownButton>
      </ButtonGroup>
      <Popper
        sx={{ zIndex: 1000 }}
        open={open}
        anchorEl={anchorEl}
        role={undefined}
        transition
        disablePortal
        placement="bottom-end"
        data-test="split-button-menu-pop-up"
        {...{ nonce: undefined, onResize: undefined, onResizeCapture: undefined }} // Note: someone on the MUI team stuffed up types and made these required.
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{ transformOrigin: placement === "bottom-end" ? "center top" : "center bottom" }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu-button" autoFocusItem data-test="list">
                  {options.map(({ label, icon }, index) => (
                    <MenuItem
                      key={index}
                      selected={index === selectedIndex}
                      onClick={handleMenuItemClick(index)}
                      data-test="item"
                    >
                      <ContentContainer $showIcon={!!icon} data-test="content-container">
                        {icon}
                        {label}
                      </ContentContainer>
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};
